import React, { useEffect, useState } from "react";
import { Stack } from "@mui/joy";
import {
  loadTossPayments,
  TossPaymentsPayment,
} from "@tosspayments/tosspayments-sdk";
import { useRecoilState, useRecoilValue } from "recoil";
import { userAtom } from "../../recoil/userAtom";
import { paymentOrderAtom } from "../../recoil/paymentOrderAtom";
import { useQuery } from "@tanstack/react-query";
import {
  PAYMENT_DOMAIN,
  PAYMENT_ORDER_DETAIL_ENDPOINT,
  paymentApi,
} from "../../api/paymentApi";
import useSafeAreaInsets from "../../hooks/useSafeAreaInsets";
import ContentTemplateV2 from "../../components/task/content/ContentTemplateV2";
import { useSearchParams } from "react-router-dom";
import {
  getMultiPlanPrices,
  getSinglePlanPrice,
  PlanKey,
  planKeyToName,
} from "../../components/task/payment/planTypes";

export default function TossPayments() {
  const tossPaymentsClientKey = process.env.REACT_APP_TOSSPAYMENTS_CLIENT_KEY!!;
  const [searchParams] = useSearchParams();
  const planItemKey = (searchParams.get("plan") || "challenger") as PlanKey;

  const [tossPaymentsPayment, setTossPaymentsPayment] =
    useState<TossPaymentsPayment | null>();

  const user = useRecoilValue(userAtom);
  const [paymentOrderState, setPaymentOrderState] =
    useRecoilState(paymentOrderAtom);

  const { refetch: getPaymentOrder } = useQuery(
    [PAYMENT_DOMAIN, PAYMENT_ORDER_DETAIL_ENDPOINT],
    () => paymentApi.getOrderDetail(),
    {
      enabled: false,
      onSuccess: (data) => {
        if (data && data.result) {
          setPaymentOrderState(data.result);
        }
      },
    }
  );

  useEffect(() => {
    if (user?.accessToken) {
      getPaymentOrder();
    } else {
      console.error("유저 정보 없음");
    }
  }, []);

  function computeAmountValue(): number {
    if (paymentOrderState.isCarePass) return 36500;

    const pricingModel = user?.pricingModel;

    // koreanmania: 모든 플랜 별도 가격
    if (pricingModel === "koreanmania") {
      if (planItemKey === "starter") return 255000;
      if (planItemKey === "challenger") return 315000;
      if (planItemKey === "master") return 385000;
    }

    // e/h/i: 단일 "맞춤 플랜"이 challenger로 매핑되며 모델별 가격 오버라이드
    const singlePlanPrice = getSinglePlanPrice(pricingModel);
    if (singlePlanPrice !== undefined && planItemKey === "challenger") {
      return singlePlanPrice;
    }

    // ajk: 다중 플랜 전체 가격 오버라이드
    const multiPlanPrices = getMultiPlanPrices(pricingModel);
    if (multiPlanPrices) {
      return multiPlanPrices[planItemKey] ?? multiPlanPrices.challenger;
    }

    // 기본 가격
    if (planItemKey === "starter") return 295000;
    if (planItemKey === "challenger") return 395000;
    if (planItemKey === "master") return 465000;
    return 395000;
  }

  async function requestTossPayments() {
    if (tossPaymentsPayment) {
      try {
        await tossPaymentsPayment?.requestPayment({
          method: "CARD",
          amount: {
            currency: "KRW",
            value: computeAmountValue(),
          },
          orderId: paymentOrderState.orderId!!,
          orderName: `디스턴싱 ${planKeyToName[planItemKey]} 플랜 / 100일${paymentOrderState?.isCarePass ? " (CarePass 적용가)" : ""}`,
          successUrl: window.location.origin + "/toss-payments/success",
          failUrl: window.location.origin + "/toss-payments/fail",
          customerEmail: paymentOrderState.customerEmail,
          customerName: paymentOrderState.customerName,
          card: {
            useEscrow: false,
            flowMode: "DEFAULT",
            useCardPoint: false,
            useAppCardOnly: false,
          },
        });
      } catch (error) {
        // 에러 처리하기
        console.error(error);
      }
    }
  }

  useEffect(() => {
    async function fetchTossPaymentsPayment() {
      if (tossPaymentsClientKey && paymentOrderState?.customerKey) {
        const tossPayments = await loadTossPayments(tossPaymentsClientKey);
        const payment = tossPayments.payment({
          customerKey: paymentOrderState.customerKey,
        });

        setTossPaymentsPayment(payment);
      }
    }

    fetchTossPaymentsPayment();
  }, [tossPaymentsClientKey, paymentOrderState]);

  useEffect(() => {
    if (tossPaymentsPayment) {
      requestTossPayments();
    }
  }, [tossPaymentsPayment]);

  const { bottom: safeBottom } = useSafeAreaInsets();
  const [containerHeight, setContainerHeight] = useState<number>(
    window.innerHeight - safeBottom
  );

  return (
    <ContentTemplateV2
      fullBleed
      disableBottomSafeArea
      isWide
      setContainerHeight={setContainerHeight}
    >
      {containerHeight > 0 && (
        <Stack sx={{ pb: "150px", opacity: 0 }}>
          <Stack
            spacing={"30px"}
            justifyContent="center"
            alignItems={"center"}
            sx={{
              width: "100%",
              maxWidth: "660px",
              mx: "auto",
              px: "20px",
            }}
          ></Stack>
        </Stack>
      )}
    </ContentTemplateV2>
  );
}
